import React, { PropsWithChildren, useEffect } from "react";
import {
  AlertDialogAction,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogTrigger,
  AlertDialog,
} from "@/components/ui";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { BULK_MESSAGES_QUERY } from "@/api/queries/bulkMessages";
import { DESTROY_BULK_MESSAGE_MUTATION } from "@/api/mutations/destroyBulkMessage";

export function DeleteButton({
  id,
  onSuccess,
  children,
}: PropsWithChildren<{
  id?: string;
  onSuccess?: () => void;
}>) {
  const { t } = useTranslation();

  const [destroyBulkMessageMutation, { data }] = useMutation(
    DESTROY_BULK_MESSAGE_MUTATION,
    { refetchQueries: [BULK_MESSAGES_QUERY] },
  );

  useEffect(() => {
    if (data?.destroyBulkMessageMutation?.success) {
      toast.success(t("bulkMessaging.table.delete.success"));
      if (onSuccess) {
        onSuccess();
      }
    } else if (data?.destroyBulkMessageMutation?.errors?.length) {
      const { errors } = data?.destroyBulkMessageMutation ?? {};
      toast.error(t("bulkMessaging.table.delete.error"), {
        description: errors?.map(({ message }) => message).join(", "),
      });
    }
  }, [data, onSuccess, t]);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("bulkMessaging.table.delete.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("bulkMessaging.table.delete.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("bulkMessaging.table.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              id &&
              destroyBulkMessageMutation({
                variables: { bulkMessageId: id },
              })
            }
          >
            {t("bulkMessaging.table.yes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
